import React, { useState, useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useNavigate,
  useLocation,
} from "react-router-dom";

import BudgetEditor from "./components/BudgetEditor";
import LoginView from "./components/LoginView";
import ChangePasswordView from "./components/ChangePasswordView";
import ResetPasswordView from "./components/ResetPasswordView";
import HeadcountEditor from "./components/HeadcountEditor";
import Dashboard from "./components/Dashboard";
import AdminPanel from "./components/AdminPanel";
import SalaryView from "./components/SalaryView";
import BIPView from "./components/BIPView";
// import TeamView from "./components/TeamView";  // replaced by ActualsDashboard
import ActualsDashboard from "./components/ActualsDashboard";

import { AuthenticatedUser, CostCenterInfo, api } from "./api";
import { trackEvent, trackPageView } from "./telemetry";
import "./components/BudgetEditor.css";
import "./App.css";

const AppContent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [authenticatedUser, setAuthenticatedUser] = useState<AuthenticatedUser | null>(() => {
    const token = localStorage.getItem("authToken");
    if (!token) return null;
    const saved = localStorage.getItem("authUser");
    return saved ? JSON.parse(saved) : null;
  });

  const isAdmin = authenticatedUser?.role === "admin";
  const hasLeadershipView = authenticatedUser?.role === "cc_leader" || authenticatedUser?.role === "bu_leader" || authenticatedUser?.role === "vp";

  // Active fiscal year from settings API
  const [activeFY, setActiveFY] = useState<string | null>(null);

  // Forecast window status for header note
  const [forecastWindowInfo, setForecastWindowInfo] = useState<{
    windowOpen: boolean;
    activeDim: string | null;
    closeDate: string | null;
    nextWindowDim: string | null;
    nextWindowDate: string | null;
  }>({ windowOpen: false, activeDim: null, closeDate: null, nextWindowDim: null, nextWindowDate: null });

  // Help URL for header button
  const [helpUrl, setHelpUrl] = useState<string>("");

  // Multi-CC support: track which CC is currently selected
  // Filter out CCs deactivated for the current FY or earlier
  const allUserCCs = authenticatedUser?.cost_centers || [];
  const fyForFilter = activeFY || (() => {
    const now = new Date();
    const yr = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    return `FY${(yr % 100).toString().padStart(2, '0')}`;
  })();
  const userCCs = allUserCCs.filter(cc => {
    if (!cc.deactivated_from_fy) return true;
    return cc.deactivated_from_fy > fyForFilter;
  });
  const [selectedCCNum, setSelectedCCNum] = useState<string | null>(() => {
    const saved = localStorage.getItem("selectedCC");
    return saved || null;
  });
  // Resolve the selected CC (fall back to owned_cost_center or first in list)
  const selectedCC: CostCenterInfo | null = (() => {
    if (userCCs.length === 0) return null;
    return userCCs.find(c => c.cost_center_number === selectedCCNum)
      || userCCs.find(c => c.cost_center_number === authenticatedUser?.owned_cost_center)
      || userCCs[0];
  })();
  const hasOwnCC = !!selectedCC;

  // Compute FY label and range from activeFY (e.g. "FY25" → April 2025 – March 2026)
  const fyLabel = activeFY || (() => {
    const now = new Date();
    const yr = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    return `FY${(yr % 100).toString().padStart(2, '0')}`;
  })();
  const fyNum = parseInt(fyLabel.replace(/\D/g, ''), 10);
  const fyStartYear = 2000 + fyNum;
  const fyEndYear = fyStartYear + 1;
  const fyRange = `April ${fyStartYear} – March ${fyEndYear}`;

  // ── Derive navigation state from URL ──────────────────────────────────────
  const pathSegment = location.pathname.split("/")[1] || "";

  useEffect(() => {
    trackPageView(document.title, `${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);

  // Admin sub-tabs
  const ADMIN_SETUP_URLS = new Set(["business-units", "cost-centers", "users", "accounts"]);
  const adminSubTab = location.pathname.split("/")[2] || "";

  // Primary tab: actuals | forecast | admin-forecast | admin-setup
  const FORECAST_URLS = new Set(["expense", "salary", "bip", "headcount"]);
  const primaryTab = pathSegment === "admin" && ADMIN_SETUP_URLS.has(adminSubTab) ? "admin-setup"
    : pathSegment === "admin" ? "admin-forecast"
    : FORECAST_URLS.has(pathSegment) ? "forecast"
    : "actuals";

  // Sub-tab: the path segment itself
  const subTab = pathSegment || "dashboard";

  // Auth is initialized from localStorage synchronously in useState above

  // Fetch active fiscal year from settings (available to all authenticated users)
  useEffect(() => {
    if (authenticatedUser) {
      api.get("/settings/fiscal-year")
        .then(r => { if (r.data.fiscalYear) setActiveFY(r.data.fiscalYear); })
        .catch(() => {});
      // Fetch role-appropriate help URL
      api.get("/settings/help-url")
        .then(r => {
          setHelpUrl(isAdmin ? (r.data.help_url_admin || "") : (r.data.help_url_user || ""));
        })
        .catch(() => {});
    }
  }, [authenticatedUser, isAdmin]);

  // Fetch forecast window config — determines if a window is currently open
  useEffect(() => {
    if (!authenticatedUser || isAdmin) return;
    const fetchWindowInfo = async () => {
      try {
        const [configRes, windowsRes] = await Promise.all([
          api.get("/settings/forecast-config"),
          api.get("/settings/dimension-windows", { params: { fy: activeFY || undefined } }),
        ]);
        const adminActive = configRes.data.active as boolean;
        const adminDim = (configRes.data.dimension || null) as string | null;

        const now = new Date();
        now.setHours(0, 0, 0, 0);
        const windows = (windowsRes.data || []) as { dimension: string; open_from: string; open_to: string }[];

        // Check if today falls within the active dimension's window
        const activeWindow = adminDim
          ? windows.find(w => w.dimension === adminDim && new Date(w.open_from) <= now && new Date(w.open_to) >= now)
          : null;
        const isWindowOpen = adminActive && !!activeWindow;

        // Close date of the currently open window
        const closeDate = activeWindow
          ? new Date(activeWindow.open_to).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
          : null;

        // Find the next future window (open_from is after today)
        const futureWindows = windows
          .filter(w => new Date(w.open_from) > now)
          .sort((a, b) => new Date(a.open_from).getTime() - new Date(b.open_from).getTime());
        const next = futureWindows[0] || null;

        setForecastWindowInfo({
          windowOpen: isWindowOpen,
          activeDim: isWindowOpen ? adminDim : null,
          closeDate,
          nextWindowDim: next?.dimension?.replace(" Forecast", "") || null,
          nextWindowDate: next ? new Date(next.open_from).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : null,
        });
      } catch { /* silent */ }
    };
    fetchWindowInfo();
  }, [authenticatedUser, isAdmin, activeFY]);

  // Listen for real-time FY changes from AdminPanel
  useEffect(() => {
    const handler = (e: Event) => setActiveFY((e as CustomEvent).detail);
    window.addEventListener("fy-changed", handler);
    return () => window.removeEventListener("fy-changed", handler);
  }, []);

  useEffect(() => {
    if (authenticatedUser) {
      if (authenticatedUser.is_first_login && location.pathname !== "/change-password") {
        navigate("/change-password", {
          state: { email: authenticatedUser.email },
        });
      } else if (location.pathname === "/login") {
        if (isAdmin) navigate("/admin/bu-dashboard");
        else if (hasOwnCC) navigate("/dashboard");
        else if (hasLeadershipView) navigate("/team");
        else navigate("/dashboard");
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticatedUser, navigate, location.pathname]);

  const handleLoginSuccess = (user: AuthenticatedUser, token: string) => {
    localStorage.setItem("authToken", token);
    localStorage.setItem("authUser", JSON.stringify(user));
    setAuthenticatedUser(user);
    trackEvent("frontend_login_success", {
      role: user.role,
      firstLogin: String(user.is_first_login),
    });
  };

  const handleSelectCC = (ccNum: string) => {
    setSelectedCCNum(ccNum);
    localStorage.setItem("selectedCC", ccNum);
  };

  const handleLogout = () => {
    if (authenticatedUser) {
      trackEvent("frontend_logout", { role: authenticatedUser.role });
    }
    localStorage.removeItem("authToken");
    localStorage.removeItem("authUser");
    localStorage.removeItem("selectedCC");
    setAuthenticatedUser(null);
    navigate("/login");
  };

  const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
    if (!authenticatedUser) return <Navigate to="/login" replace />;
    return <>{children}</>;
  };

  // Header role label
  const roleLabel = isAdmin ? "Admin"
    : authenticatedUser?.role === "vp" ? "VP"
    : authenticatedUser?.role === "bu_leader" ? "BU Leader"
    : authenticatedUser?.role === "cc_leader" ? "CC Leader"
    : "";

  // Whether CC pills should show (My Cost Centers or Forecast views, not My Team)
  const showCCPills = !isAdmin && userCCs.length > 1 && subTab !== "team";

  // Whether scope toggle row should show (Actuals mode, both CC and team available)
  const showScopeRow = !isAdmin && primaryTab === "actuals" && hasOwnCC && hasLeadershipView;

  // Page title for header row 2
  const PAGE_TITLES: Record<string, string> = {
    dashboard: "Monthly Spend Review",
    team: "Team Overview",
  };
  const pageTitle = PAGE_TITLES[subTab] || "";
  const showHeaderRow2 = !isAdmin && authenticatedUser && (pageTitle || showScopeRow);

  const AppLayout = () => (
    <div>
      <div className={`app-header${showHeaderRow2 ? " app-header--has-row2" : ""}`}>
        {/* Row 1 left: title + meta */}
        <div className="app-header-left">
          <span className="app-header-title">
            QFT {roleLabel && `· ${roleLabel}`}
            <span className="fy-badge">{fyLabel}</span>
            <span className="fy-range">({fyRange})</span>
          </span>
          {authenticatedUser && (
            <span className="app-header-meta">
              <span className="app-header-meta-label">User</span>
              {authenticatedUser.name}
              {hasOwnCC && userCCs.length <= 1 && (
                <>
                  <span className="app-header-meta-sep">·</span>
                  <span className="app-header-meta-label">CC</span>
                  {selectedCC!.cost_center_number} – {selectedCC!.cost_center_name}
                </>
              )}
            </span>
          )}
        </div>

        {/* Budgeting Window status (forecast pages) */}
        {!isAdmin && primaryTab === "forecast" && (
          <div className={`forecast-window-note ${forecastWindowInfo.windowOpen ? "forecast-window-note--open" : "forecast-window-note--closed"}`}>
            <div className="fw-row">
              <span className="fw-label">Budgeting Window</span>
              {forecastWindowInfo.windowOpen ? (
                <span className="fw-status fw-status--open">Open</span>
              ) : (
                <span className="fw-status fw-status--closed">Closed</span>
              )}
            </div>
            <div className="fw-row-detail">
              {forecastWindowInfo.windowOpen ? (
                <span className="fw-detail">
                  {forecastWindowInfo.activeDim?.replace(" Forecast", "")} Forecast closes <strong>{forecastWindowInfo.closeDate}</strong>
                </span>
              ) : forecastWindowInfo.nextWindowDate && forecastWindowInfo.nextWindowDim ? (
                <span className="fw-detail">
                  {forecastWindowInfo.nextWindowDim} Forecast opens <strong>{forecastWindowInfo.nextWindowDate}</strong>
                </span>
              ) : (
                <span className="fw-detail">No upcoming windows scheduled</span>
              )}
            </div>
          </div>
        )}

        {/* Row 1 center: primary nav tabs */}
        {!isAdmin && (
          <div className="nav-tabs app-header-nav">
            <button
              onClick={() => navigate(hasOwnCC ? "/dashboard" : "/team")}
              className={`nav-tab${primaryTab === "actuals" ? " nav-tab--active" : ""}`}
            >
              <span className="material-icons">bar_chart</span>
              Actuals
            </button>
            {hasOwnCC && (
              <button
                onClick={() => navigate("/expense")}
                className={`nav-tab${primaryTab === "forecast" ? " nav-tab--active" : ""}`}
              >
                <span className="material-icons">trending_up</span>
                Forecast
              </button>
            )}
          </div>
        )}

        {isAdmin && (
          <div className="nav-tabs app-header-nav">
            <button
              onClick={() => navigate("/admin/bu-dashboard")}
              className={`nav-tab${primaryTab === "admin-forecast" ? " nav-tab--active" : ""}`}
            >
              <span className="material-icons">tune</span>
              Forecast Setup
            </button>
            <button
              onClick={() => navigate("/admin/business-units")}
              className={`nav-tab${primaryTab === "admin-setup" ? " nav-tab--active" : ""}`}
            >
              <span className="material-icons">admin_panel_settings</span>
              Admin Setup
            </button>
          </div>
        )}

        {/* Row 1 right: help + logout */}
        <div className="app-header-actions">
          <button
            onClick={() => {
              if (helpUrl) window.open(helpUrl, "_blank", "noopener");
              else alert(isAdmin ? "Configure help URLs in Forecast Setup > Forecast Windows." : "Help page not configured yet. Contact your admin.");
            }}
            className="help-btn"
            title="Help"
          >
            <span className="material-icons">help_outline</span>
            Help
          </button>
          <button onClick={handleLogout} className="logout-btn">
            <span className="material-icons">logout</span>
            Logout
          </button>
        </div>

        {/* Row 2 left: page title + subtitle */}
        {showHeaderRow2 && pageTitle && (
          <div className="app-header-page-title-group">
            <span className="app-header-page-title">{pageTitle}</span>
            <div id="page-subtitle-slot" />
          </div>
        )}

        {/* Row 2 center: scope toggle (aligned with nav-tabs above) */}
        {showHeaderRow2 && showScopeRow && (
          <div className="scope-tabs app-header-scope">
            <button
              onClick={() => navigate("/dashboard")}
              className={`scope-tab${subTab === "dashboard" ? " scope-tab--active" : ""}`}
            >
              <span className="material-icons">account_balance</span>
              My Cost Centers
            </button>
            <button
              onClick={() => navigate("/team")}
              className={`scope-tab${subTab === "team" ? " scope-tab--active" : ""}`}
            >
              <span className="material-icons">groups</span>
              My Team
            </button>
          </div>
        )}
      </div>

      {/* Forecast sub-tabs */}
      {!isAdmin && primaryTab === "forecast" && (
        <div className="app-sub-nav">
          <button
            onClick={() => navigate("/expense")}
            className={`nav-tab nav-tab--sub nav-tab--sub-expense${subTab === "expense" ? " nav-tab--sub-active" : ""}`}
          >
            <span className="material-icons">receipt_long</span>
            Expense
          </button>
          <button
            onClick={() => navigate("/salary")}
            className={`nav-tab nav-tab--sub nav-tab--sub-salary${subTab === "salary" ? " nav-tab--sub-active" : ""}`}
          >
            <span className="material-icons">payments</span>
            Salary
          </button>
          <button
            onClick={() => navigate("/bip")}
            className={`nav-tab nav-tab--sub nav-tab--sub-bip${subTab === "bip" ? " nav-tab--sub-active" : ""}`}
          >
            <span className="material-icons">emoji_events</span>
            BIP
          </button>
          <button
            onClick={() => navigate("/headcount")}
            className={`nav-tab nav-tab--sub nav-tab--sub-headcount${subTab === "headcount" ? " nav-tab--sub-active" : ""}`}
          >
            <span className="material-icons">group</span>
            Headcount
          </button>
        </div>
      )}

      <div style={{ padding: "0 16px" }}>
        {/* Content controls row: CC pills (left) + page controls slot (right) */}
        {!isAdmin && authenticatedUser && (
          <div className="cc-content-row">
            {showCCPills && (
              <div className="cc-selector">
                {userCCs.map(cc => (
                  <button
                    key={cc.cost_center_number}
                    className={`cc-pill${cc.cost_center_number === selectedCC?.cost_center_number ? " cc-pill--active" : ""}`}
                    onClick={() => handleSelectCC(cc.cost_center_number)}
                    title={cc.cost_center_name}
                  >
                    {cc.cost_center_number} – {cc.cost_center_name}
                  </button>
                ))}
              </div>
            )}
            <div id="page-controls-slot" />
          </div>
        )}
        <Outlet />
      </div>
    </div>
  );

  return (
    <div className="app-container">
      <Routes>
        <Route path="/login" element={<LoginView onLoginSuccess={handleLoginSuccess} />} />
        <Route path="/change-password" element={<ChangePasswordView />} />
        <Route path="/reset-password" element={<ResetPasswordView />} />

        <Route
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          {/* Admin views */}
          <Route path="/admin" element={<Navigate to="/admin/bu-dashboard" replace />} />
          <Route path="/admin/:tab" element={<AdminPanel />} />

          {/* Actuals > My Team (Actuals Dashboard scoped to user's BUs/CCs) */}
          <Route
            path="/team"
            element={
              authenticatedUser && (hasLeadershipView || isAdmin)
                ? <ActualsDashboard apiPrefix="/team" fiscalYear={activeFY || fyLabel} />
                : <Navigate to="/dashboard" replace />
            }
          />

          {/* Actuals > My Cost Centers */}
          <Route
            path="/dashboard"
            element={
              authenticatedUser && hasOwnCC
                ? <Dashboard costCenterNumber={selectedCC!.cost_center_number} costCenterName={selectedCC!.cost_center_name} />
                : null
            }
          />

          {/* Forecast > Expense */}
          <Route
            path="/expense"
            element={
              authenticatedUser && hasOwnCC
                ? <BudgetEditor costCenterNumber={selectedCC!.cost_center_number} costCenterName={selectedCC!.cost_center_name} costCenterOwner={selectedCC!.cost_center_owner} fiscalYear={fyLabel} />
                : null
            }
          />
          <Route
            path="/headcount"
            element={
              authenticatedUser && hasOwnCC
                ? <HeadcountEditor costCenterNumber={selectedCC!.cost_center_number} />
                : null
            }
          />
          <Route
            path="/salary"
            element={
              authenticatedUser && hasOwnCC
                ? <SalaryView costCenterNumber={selectedCC!.cost_center_number} />
                : null
            }
          />
          <Route
            path="/bip"
            element={
              authenticatedUser && hasOwnCC
                ? <BIPView costCenterNumber={selectedCC!.cost_center_number} />
                : null
            }
          />

          {/* Root redirect */}
          <Route path="/" element={
            <Navigate to={
              isAdmin ? "/admin/forecast-windows"
              : hasOwnCC ? "/dashboard"
              : hasLeadershipView ? "/team"
              : "/dashboard"
            } replace />
          } />
        </Route>

        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </div>
  );
};

const App: React.FC = () => (
  <BrowserRouter>
    <AppContent />
  </BrowserRouter>
);

export default App;
